import React, { useEffect, useState, useRef, useMemo } from 'react';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';

// Export the color determination function so it can be used in AttemptCard
export const getScoreColor = (score) => {
  if (score > 75) {
    return {
      main: 'limegreen',
      hover: 'green-400',
      shadow: 'green-200'
    };
  } else if (score > 40) {
    return {
      main: 'royalblue',
      hover: 'blue-400',
      shadow: 'blue-200'
    };
  } else {
    return {
      main: 'red',
      hover: 'red-400',
      shadow: 'red-200'
    };
  }
};

function CircularProgressChart({ score, index, session }) {
  // Store the initial score in a ref to prevent animation on re-renders
  const initialRenderRef = useRef(true);
  const previousScoreRef = useRef(score); // Add a ref to track score changes
  const [currentValue, setCurrentValue] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [rotation, setRotation] = useState(0);
  
  // Check if score is a number or "About to play"
  const isPlayMode = typeof score !== 'number' || isNaN(score);

  // Calculate average score
  const averageScore = useMemo(() => {
    // Removed attempts dependency as it's not used in this component
    return 0;
  }, []);

  const getChartColors = () => {
    // If in play mode, use a neutral color
    if (isPlayMode) {
      return {
        path: 'url(#playGradient)',
        trail: '#f1f5f9', // slate-100
        shadow: '0 0 15px #6b7280', // gray-500
        glow: '0 0 20px #6b7280, 0 0 40px #6b7280' // gray-500
      };
    }
    
    let pathColor, trailColor;
    
    if (currentValue > 75) {
      pathColor = 'url(#greenGradient)';
      trailColor = '#dcfce7'; // green-100
    } else if (currentValue > 40) {
      pathColor = 'url(#blueGradient)';
      trailColor = '#dbeafe'; // blue-100
    } else {
      pathColor = 'url(#orangeGradient)';
      trailColor = '#fee2e2'; // red-100
    }

    return {
      path: pathColor,
      trail: trailColor,
      shadow: `0 0 15px ${currentValue > 75 ? 'limegreen' : currentValue > 40 ? 'royalblue' : 'darkorange'}`,
      glow: `0 0 20px ${currentValue > 75 ? 'limegreen' : currentValue > 40 ? 'royalblue' : 'darkorange'}, 
             0 0 40px ${currentValue > 75 ? 'limegreen' : currentValue > 40 ? 'royalblue' : 'darkorange'}`
    };
  };

  // Force a reset when the score changes
  useEffect(() => {
    // Reset animation state
    setCurrentValue(0);
    initialRenderRef.current = true;
    previousScoreRef.current = score;
    
    // Log for debugging
    console.log(`Chart reset with score: ${score}`);
  }, [score, session]);  // Add score and session to dependency array to ensure chart resets when score or session changes

  useEffect(() => {
    // Skip animation for non-numeric scores or if it's not the initial render
    if (isPlayMode || !initialRenderRef.current) {
      setCurrentValue(isPlayMode ? 0 : score);
      return;
    }

    // Set flag to indicate initial render is complete
    initialRenderRef.current = false;
    
    // Instead of always resetting to 0, set an initial value closer to the target
    // to prevent it from appearing as 0 briefly when switching
    if (currentValue === 0 || Math.abs(currentValue - score) > 15) {
      setCurrentValue(Math.max(score * 0.5, 1)); // Start at 50% of the score value
    }
    
    // Animate the progress to the target score with easing
    const timer = setInterval(() => {
      if (Math.abs(currentValue - score) > 0.5) {  // Continue animation until we're close enough
        setCurrentValue(prev => {
          const remaining = score - prev;
          const increment = remaining * 0.25; // Faster acceleration (was 0.15)
          return prev + increment;
        });
      } else {
        setCurrentValue(score); // Ensure we reach the exact score value
      }
    }, 16);

    return () => clearInterval(timer);
  }, [score, currentValue, session, isPlayMode]);

  // Subtle continuous rotation animation - disabled for stability
  // useEffect(() => {
  //   const rotationTimer = setInterval(() => {
  //     setRotation(prev => (prev + 0.2) % 360);
  //   }, 50);

  //   return () => clearInterval(rotationTimer);
  // }, []);

  const colors = getChartColors(index);

  return (
    <div 
      key={`${session}-${score}`} // Add a key prop to force a re-render when the session or score changes
      className="relative w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 mx-auto mb-2 sm:mb-3 transition-all duration-300"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        transform: `scale(${isHovered ? 1.1 : 1}) perspective(1000px) rotateY(${isHovered ? 10 : 0}deg)`,
        filter: isHovered ? colors.shadow : 'none'
      }}
    >
      <svg style={{ height: 0 }}>
        <defs>
          <linearGradient id="greenGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="limegreen" />
            <stop offset="50%" stopColor="forestgreen" />
            <stop offset="100%" stopColor="darkgreen" />
          </linearGradient>
          <linearGradient id="blueGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="royalblue" />
            <stop offset="50%" stopColor="mediumblue" />
            <stop offset="100%" stopColor="darkblue" />
          </linearGradient>
          <linearGradient id="orangeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="red" />
            <stop offset="50%" stopColor="red" />
            <stop offset="100%" stopColor="red" />
          </linearGradient>
          <linearGradient id="playGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#6b7280" /> {/* gray-500 */}
            <stop offset="50%" stopColor="#4b5563" /> {/* gray-600 */}
            <stop offset="100%" stopColor="#374151" /> {/* gray-700 */}
          </linearGradient>
        </defs>
      </svg>

      {/* Outer Glow */}
      <div 
        className="absolute inset-0 rounded-full transition-opacity duration-300"
        style={{
          boxShadow: isHovered ? '0 2px 12px 2px #bfc9d1' : 'none',
          opacity: isHovered ? 0.4 : 0,
          zIndex: 0
        }}
      />

      <div className="relative z-10">
        <CircularProgressbar
          value={isPlayMode ? 100 : currentValue}
          text={isPlayMode ? "PLAY" : `${Math.round(currentValue)}%`}
          styles={buildStyles({
            textSize: isPlayMode ? '20px' : '26px',
            pathColor: colors.path,
            textColor: isHovered ? 'midnightblue' : 'slategray',
            trailColor: colors.trail,
            pathTransition: 'stroke-dashoffset 0.5s ease',
            rotation: rotation / 360,
          })}
        />
      </div>

      {/* Background Pulse Effect */}
      <div 
        className="absolute inset-0 rounded-full opacity-20 transition-all duration-1000"
        style={{
          background: colors.path,
          transform: `scale(${isHovered ? 1.2 : 1})`,
          filter: 'blur(8px)',
          zIndex: -1,
        }}
      />

      {/* Inner Shadow */}
      <div 
        className="absolute inset-0 rounded-full transition-all duration-300"
        style={{
          boxShadow: 'inset 0 0 10px rgba(0,0,0,0.2)',
          opacity: isHovered ? 0.3 : 0.1,
          zIndex: 1
        }}
      />
    </div>
  );
}

export default CircularProgressChart;